import React, { useEffect, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import LoadingSpinner from 'components_new/atoms/LoadingSpinner'
import Text from 'components_new/atoms/Text'

import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { useDataRerunsActions } from 'redux/actions/internal/DataReruns'
import { useInternalDataReruns } from 'redux/hooks/internal/DataReruns'
import { RerunCandidate } from 'redux/selectors/internal/DataReruns'

import RerunConfirmStep from './ConfirmStep'
import RerunCustomerRow from './CustomerRow'
import { countJobs, orderByPriority, RerunRow } from './lib'

interface RerunDialogProps {
  /** Scope the candidates are loaded for, null = all customers. */
  customerId: string | null
  onClose: () => void
  onCreated: (rerunId: string) => void
  open: boolean
  /** The only customer selected from the start, else all of them. */
  preselectedCustomerId?: string | null
}

type Step = 'select' | 'confirm'

function toggled(
  record: Record<string, boolean>,
  key: string,
  current: boolean
): Record<string, boolean> {
  return { ...record, [key]: !current }
}

const RerunDialog = (props: RerunDialogProps) => {
  const { customerId, onClose, onCreated, open, preselectedCustomerId } = props

  const reruns = useInternalDataReruns()
  const { tryCreateRerun, tryGetRerunCandidates } = useDataRerunsActions()

  const [step, setStep] = useState<Step>('select')
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [priority, setPriority] = useState<Record<string, boolean>>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [excludedJobs, setExcludedJobs] = useState<Record<string, string[]>>({})
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (!open) {
      return
    }

    setStep('select')
    setSelected({})
    setPriority({})
    setExpanded({})
    setExcludedJobs({})
    setDescription('')
    tryGetRerunCandidates(customerId ?? undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const candidates = reruns.candidates?.customers ?? []
  const windowSize = reruns.candidates?.window_size ?? 0

  const isSelectable = (candidate: RerunCandidate) => !candidate.queued_in_rerun
  const isSelected = (candidate: RerunCandidate) =>
    isSelectable(candidate) &&
    (selected[candidate.customer_id] ??
      (!preselectedCustomerId ||
        candidate.customer_id === preselectedCustomerId))
  const isPriority = (candidate: RerunCandidate) =>
    !!priority[candidate.customer_id]
  const jobNamesOf = (candidate: RerunCandidate) =>
    candidate.collection_jobs
      .map((job) => job.job_name)
      .filter(
        (jobName) => !excludedJobs[candidate.customer_id]?.includes(jobName)
      )

  const ordered = orderByPriority(
    candidates,
    (candidate) => candidate.customer_name,
    isPriority
  )
  const rows: RerunRow[] = ordered.filter(isSelected).map((candidate) => ({
    candidate,
    jobNames: jobNamesOf(candidate),
    priority: isPriority(candidate)
  }))
  const selectable = candidates.filter(isSelectable)
  const allSelected = rows.length === selectable.length
  const hasCollision = rows.some((row) => row.candidate.sim_running)

  const toggleAll = () => {
    setSelected(
      Object.fromEntries(
        selectable.map((candidate) => [candidate.customer_id, !allSelected])
      )
    )
  }

  const toggleJob = (candidate: RerunCandidate, jobName: string) => {
    const excluded = excludedJobs[candidate.customer_id] ?? []

    setExcludedJobs({
      ...excludedJobs,
      [candidate.customer_id]: excluded.includes(jobName)
        ? excluded.filter((name) => name !== jobName)
        : [...excluded, jobName]
    })
  }

  const submit = () => {
    tryCreateRerun(
      {
        customers: rows.map((row) => ({
          customer_id: row.candidate.customer_id,
          job_names: row.jobNames,
          priority: row.priority
        })),
        description
      },
      (err, rerunId) => {
        if (!err && rerunId) {
          onCreated(rerunId)
          onClose()
        }
      }
    )
  }

  const scopedCandidate =
    customerId && candidates.length === 1 ? candidates[0] : null
  const title = scopedCandidate
    ? `Kör om ${scopedCandidate.customer_name}`
    : 'Kör om alla kunder'

  const selectActions = (
    <>
      <Button onClick={onClose} variant="text">
        Avbryt
      </Button>
      <Button
        disabled={rows.length === 0}
        onClick={() => setStep('confirm')}
        variant="contained"
      >
        Fortsätt
      </Button>
    </>
  )

  const confirmActions = (
    <>
      <Button onClick={() => setStep('select')} variant="text">
        Tillbaka
      </Button>
      <Button
        color={hasCollision ? 'warning' : undefined}
        loading={reruns.creating}
        onClick={submit}
        variant="contained"
      >
        {hasCollision ? 'Kör om ändå' : 'Starta omkörning'}
      </Button>
    </>
  )

  const renderSelect = () => {
    if (reruns.candidatesFetching) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <LoadingSpinner />
        </Box>
      )
    }

    if (candidates.length === 0) {
      return (
        <Text sx={{ color: 'text.secondary', py: 4 }} variant="body2">
          {reruns.candidates
            ? 'Det finns inga kunder att köra om.'
            : 'Kunde inte hämta kunderna.'}
        </Text>
      )
    }

    return (
      <>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            mb: 1
          }}
        >
          <Text sx={{ color: 'text.secondary' }} variant="body2">
            {rows.length} kunder · {countJobs(rows)} jobb
          </Text>
          <Button
            disabled={selectable.length === 0}
            onClick={toggleAll}
            size="small"
            variant="text"
          >
            {allSelected ? 'Välj bort alla' : 'Välj alla'}
          </Button>
        </Box>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflowY: 'auto'
          }}
        >
          {ordered.map((candidate) => (
            <RerunCustomerRow
              candidate={candidate}
              disabled={!isSelectable(candidate)}
              expanded={!!expanded[candidate.customer_id]}
              jobNames={jobNamesOf(candidate)}
              key={candidate.customer_id}
              onToggle={() =>
                setSelected(
                  toggled(
                    selected,
                    candidate.customer_id,
                    isSelected(candidate)
                  )
                )
              }
              onToggleExpanded={() =>
                setExpanded(
                  toggled(
                    expanded,
                    candidate.customer_id,
                    !!expanded[candidate.customer_id]
                  )
                )
              }
              onToggleJob={(jobName) => toggleJob(candidate, jobName)}
              onTogglePriority={() =>
                setPriority(
                  toggled(
                    priority,
                    candidate.customer_id,
                    isPriority(candidate)
                  )
                )
              }
              priority={isPriority(candidate)}
              selected={isSelected(candidate)}
            />
          ))}
        </Box>
        <Text sx={{ color: 'text.secondary', mt: 1.5 }} variant="caption">
          Kunder markerade med stjärna går först, övriga körs i alfabetisk
          ordning.
        </Text>
      </>
    )
  }

  return (
    <SimpleDialog
      actions={step === 'select' ? selectActions : confirmActions}
      hasClose
      helperText={
        step === 'select'
          ? 'Varje kund körs hela vägen: valda insamlingsjobb → SIM → nedströms.'
          : 'Bekräfta omfattningen innan körningen startar.'
      }
      maxWidth="md"
      onClose={onClose}
      open={open}
      title={title}
    >
      {step === 'select' ? (
        renderSelect()
      ) : (
        <RerunConfirmStep
          description={description}
          onDescriptionChange={setDescription}
          rows={rows}
          windowSize={windowSize}
        />
      )}
    </SimpleDialog>
  )
}

RerunDialog.displayName = 'RerunDialog'
export default RerunDialog
